/**
 * **Sparks**. The tinder over the throw. Ported from Armarium
 * (`apps/web/src/lib/dice3d/sparks.ts`).
 *
 * A `THREE.PointsMaterial` without a texture draws **squares**: at eight points
 * nobody notices, at sixty it looks like confetti cut from paper. Sparks are
 * round, white inside and nothing outside. That is not a picture, it is a
 * formula, and it lives in the fragment step below.
 *
 * One single point cloud carries every spark on the stage: the spray at the
 * wall, the shower on landing, the firework of a natural 20. Whatever is no
 * longer alive gets size zero and costs nothing further. So there is no
 * allocation mid-throw. Which would be a stutter exactly where you're looking.
 */

import * as THREE from 'three';

const CAPACITY = 420;

/**
 * `uScale` is `frameHeight / (2·tan(halfFov))`. The conversion from world
 * units to pixels at camera distance 1. A guessed number here once made the
 * sparks two pixels across: present in the arithmetic, absent to the eye.
 */
const VERTEX = /* glsl */ `
  uniform float uScale;
  attribute float aSize;
  attribute vec3 aTint;
  attribute float aFade;
  varying vec3 vTint;
  varying float vFade;
  void main() {
    vTint = aTint;
    vFade = aFade;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = max(1.0, aSize * uScale / max(0.001, -mv.z));
    gl_Position = projectionMatrix * mv;
  }
`;

/**
 * **Painted, not added.**
 *
 * Additive glow is the usual thing for sparks, and on parchment it is nothing:
 * gold plus cream is cream. So a spark has to *cover* rather than shine.
 * gold leaf, not a phosphor. It stays round all the same, and it still has a
 * brighter core: that now sits in the opacity and in a lightening toward the
 * middle rather than in the sum with the background.
 */
const FRAGMENT = /* glsl */ `
  varying vec3 vTint;
  varying float vFade;
  void main() {
    float d = length(gl_PointCoord - vec2(0.5)) * 2.0;
    float a = 1.0 - smoothstep(0.0, 1.0, d);
    a *= a;
    if (a * vFade < 0.01) discard;
    gl_FragColor = vec4(mix(vTint, vTint + vec3(0.45), a * a), a * vFade);
  }
`;

const HOT = new THREE.Color();
const COOL = new THREE.Color();
const MIX = new THREE.Color();
const DIR = new THREE.Vector3();
const WOBBLE = new THREE.Vector3();

export class Sparks {
  constructor(scene, floorY) {
    this.floorY = floorY;
    this.velocity = new Float32Array(CAPACITY * 3);
    this.age = new Float32Array(CAPACITY);
    this.life = new Float32Array(CAPACITY);
    this.size0 = new Float32Array(CAPACITY);
    this.tint0 = new Float32Array(CAPACITY * 3);
    this.drag = new Float32Array(CAPACITY);
    this.gravity = new Float32Array(CAPACITY);
    /** Where the next spark is written. The ring buffer. */
    this.next = 0;
    this.alive = 0;

    const geometry = new THREE.BufferGeometry();
    this.position = new Float32Array(CAPACITY * 3);
    this.size = new Float32Array(CAPACITY);
    this.tint = new Float32Array(CAPACITY * 3);
    this.fade = new Float32Array(CAPACITY);
    geometry.setAttribute('position', new THREE.BufferAttribute(this.position, 3));
    geometry.setAttribute('aSize', new THREE.BufferAttribute(this.size, 1));
    geometry.setAttribute('aTint', new THREE.BufferAttribute(this.tint, 3));
    geometry.setAttribute('aFade', new THREE.BufferAttribute(this.fade, 1));
    // The bounding sphere is only there so three doesn't cull sparks one by
    // one: they fly far, the cloud as a whole is always in frame.
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 40);

    this.points = new THREE.Points(geometry, new THREE.ShaderMaterial({
      uniforms: { uScale: { value: 900 } },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthWrite: false,
      // **Sparks are not occluded.** They are born at the body, so mostly
      // *inside* it; with a depth test nearly every one falls behind the die
      // and is gone. The gold dust belongs on top.
      depthTest: false,
    }));
    this.points.frustumCulled = false;
    this.points.visible = false;
    scene.add(this.points);
  }

  /** Frame height and field of view change the conversion. Both come from outside. */
  setViewport(heightPx, fovDegrees) {
    this.points.material.uniforms.uScale.value = heightPx / (2 * Math.tan((fovDegrees * Math.PI) / 360));
  }

  emit(burst) {
    HOT.set(burst.hot);
    COOL.set(burst.cool);
    DIR.set(burst.dir[0], burst.dir[1], burst.dir[2]).normalize();
    for (let n = 0; n < burst.count; n++) {
      const i = this.next;
      this.next = (this.next + 1) % CAPACITY;
      if (this.age[i] >= this.life[i]) this.alive++;

      // A direction inside the cone: a random direction pulled toward the main
      // one. `spread` decides how hard it is pulled.
      const t = Math.random() * Math.PI * 2;
      const c = Math.random() * 2 - 1;
      const r = Math.sqrt(1 - c * c);
      WOBBLE.set(r * Math.cos(t), c, r * Math.sin(t));
      WOBBLE.lerp(DIR, 1 - burst.spread).normalize();

      const speed = burst.speed * (0.4 + Math.random() * 1.1);
      // How far from the origin the sparks are **born**. Born from a point they
      // look like a blob for the first instant; born from a shell they are a
      // cloud from the start.
      const born = (burst.shell ?? 0.06) * (0.35 + Math.random() * 0.65);
      this.position[i * 3] = burst.at[0] + WOBBLE.x * born;
      this.position[i * 3 + 1] = burst.at[1] + WOBBLE.y * born;
      this.position[i * 3 + 2] = burst.at[2] + WOBBLE.z * born;
      this.velocity[i * 3] = WOBBLE.x * speed;
      this.velocity[i * 3 + 1] = WOBBLE.y * speed;
      this.velocity[i * 3 + 2] = WOBBLE.z * speed;

      this.age[i] = 0;
      this.life[i] = burst.life * (0.6 + Math.random() * 0.7);
      this.size0[i] = burst.size * (0.5 + Math.random() * 1.1);
      // How hard the air brakes. **The actual dial for range**: a spark travels
      // roughly `speed / drag`, however long it lives.
      this.drag[i] = (burst.drag ?? 2.5) * (0.75 + Math.random() * 0.5);
      this.gravity[i] = burst.gravity;

      MIX.copy(COOL).lerp(HOT, Math.random() ** 0.6).toArray(this.tint0, i * 3);
    }
  }

  step(dt) {
    if (this.alive === 0) {
      this.points.visible = false;
      return;
    }
    this.points.visible = true;
    let alive = 0;
    for (let i = 0; i < CAPACITY; i++) {
      if (this.age[i] >= this.life[i]) {
        this.size[i] = 0;
        continue;
      }
      this.age[i] += dt;
      const u = this.age[i] / this.life[i];
      if (u >= 1) {
        this.size[i] = 0;
        continue;
      }
      alive++;

      const slow = Math.exp(-this.drag[i] * dt);
      this.velocity[i * 3] *= slow;
      this.velocity[i * 3 + 1] = this.velocity[i * 3 + 1] * slow - this.gravity[i] * dt;
      this.velocity[i * 3 + 2] *= slow;
      this.position[i * 3] += this.velocity[i * 3] * dt;
      this.position[i * 3 + 1] += this.velocity[i * 3 + 1] * dt;
      this.position[i * 3 + 2] += this.velocity[i * 3 + 2] * dt;

      // **The table holds sparks too.** Without this line they fall through it,
      // and what should look like tinder skidding over the wood looks like
      // tinder the wood swallowed.
      if (this.position[i * 3 + 1] < this.floorY) {
        this.position[i * 3 + 1] = this.floorY;
        this.velocity[i * 3 + 1] = Math.abs(this.velocity[i * 3 + 1]) * 0.3;
      }

      // A spark fades but barely shrinks: shrinking along with the brightness
      // would make it one pixel in a blink and therefore gone. What keeps it
      // alive is the flicker. It glows. **The colour stays**, only the opacity
      // goes.
      const flicker = 0.72 + 0.28 * Math.sin((u * 34 + i) * 2.1);
      this.size[i] = this.size0[i] * (0.45 + 0.55 * (1 - u)) * flicker;
      this.fade[i] = Math.pow(1 - u, 1.15) * flicker;
      this.tint[i * 3] = this.tint0[i * 3];
      this.tint[i * 3 + 1] = this.tint0[i * 3 + 1];
      this.tint[i * 3 + 2] = this.tint0[i * 3 + 2];
    }
    this.alive = alive;
    const g = this.points.geometry;
    g.getAttribute('position').needsUpdate = true;
    g.getAttribute('aSize').needsUpdate = true;
    g.getAttribute('aTint').needsUpdate = true;
    g.getAttribute('aFade').needsUpdate = true;
  }

  /**
   * All glow out at once. An age beyond any lifetime means "dead" everywhere.
   */
  clear() {
    this.age.fill(Number.POSITIVE_INFINITY);
    this.size.fill(0);
    this.alive = 0;
    this.points.visible = false;
  }
}
